/** Screen fail rate: monthly bars + cumulative trend line + 50% feasibility reference. */
import { useCallback } from "react";
import * as d3 from "d3";
import {
  COLORS,
  useD3Chart,
  drawBandAxes,
  drawBandBars,
  monthKey,
  FONT,
  type Margin,
} from "./chartBase";
import type { ScreenFailRateChartProps } from "./onboardingPlots.types";

export default function ScreenFailRateChart({
  rows,
  assumption = 0.5,
  height = 300,
}: ScreenFailRateChartProps) {
  const draw = useCallback(
    (svg, tip, W) => {
      const H = height;
      const m: Margin = { top: 16, right: 16, bottom: 42, left: 40 };
      const d = rows
        .filter((r) => r.screenedPerMonth != null)
        .map((r) => ({ ...r, mm: monthKey(r.date) }));
      if (!d.length) return;

      const x = d3
        .scaleBand()
        .domain(d.map((r) => r.mm))
        .range([m.left, W - m.right])
        .padding(0.3);
      const y = d3
        .scaleLinear()
        .domain([0, 1])
        .range([H - m.bottom, m.top]);
      drawBandAxes(svg, W, H, m, x, y, d3.format(".0%"));

      drawBandBars(
        svg,
        d,
        x,
        y,
        // Missing monthly screen-fail values are rendered as zero so the
        // cumulative line starts at the baseline instead of dropping out.
        (r) => r.screenFailRatePerMonth ?? 0,
        COLORS.sfBar,
        tip,
        (r) =>
          `<b>${r.date}</b><br>Monthly SF: <b>${Math.round((r.screenFailRatePerMonth ?? 0) * 100)}%</b>`,
      );

      svg
        .append("line")
        .attr("x1", m.left)
        .attr("x2", W - m.right)
        .attr("y1", y(assumption))
        .attr("y2", y(assumption))
        .attr("stroke", COLORS.tooltipBg)
        .attr("stroke-dasharray", "4 4")
        .attr("opacity", 0.5);
      svg
        .append("text")
        .attr("x", W - m.right)
        .attr("y", y(assumption) - 4)
        .attr("text-anchor", "end")
        .attr("fill", COLORS.axis)
        .style("font-size", "10.5px")
        .style("font-weight", 600)
        .style("font-family", FONT)
        .text(`${Math.round(assumption * 100)}% assumption`);

      const cx = (r: any) => (x(r.mm) as number) + x.bandwidth() / 2;
      svg
        .append("path")
        .datum(d)
        .attr("fill", "none")
        .attr("stroke", COLORS.red)
        .attr("stroke-width", 2.6)
        .attr(
          "d",
          d3
            .line<any>()
            .x(cx)
            .y((r) => y(r.screenFailRateCumulative ?? 0)),
        );
      svg
        .selectAll("circle")
        .data(d)
        .join("circle")
        .attr("cx", cx)
        .attr("cy", (r: any) => y(r.screenFailRateCumulative ?? 0))
        .attr("r", 3)
        .attr("fill", COLORS.red);
    },
    [rows, height, assumption],
  );
  const { ref, svgRef, width } = useD3Chart(draw);

  return (
    <div
      ref={ref}
      style={{ position: "relative", width: "100%", fontFamily: FONT }}
    >
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
}
